import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import MediaPlayer from '../../../ui/MediaPlayer';
import { Button, Figure, Typo, Modal, Flex } from '../../../ui';
import ConfirmDelete from '../../../ui/Modal/ConfirmDelete';
import { media } from '../../../utils/media';
import { useFileCourseInfo } from '../hooks/useMediaState';
import { useMediaActions } from '../hooks/useMediaActions';
import { useTranslation } from 'react-i18next';
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import DownloadableFile from '../../../ui/DownloadableFile';
import theme from '../../../ui/theme';
import { toRgba } from '../../../ui/theme/colors';

const Image = styled(Figure.Landscape)`
  background: repeating-conic-gradient(${props => props.theme.colors.NEU_50} 0% 25%, transparent 0% 50%) 0% / 25px 25px;
  img { object-fit: contain; }
`;

const MyDocViewer = styled(DocViewer)`
  min-height:600px;
  ${media.lessThan('phablet')} {
    max-height:440px;
  }
`;

const FileContent = styled.div`
  background-color: ${toRgba(theme.colors.NEU_0, 0.3)};
  width: 100%;
  padding-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  ${media.lessThan('phablet')} {
    flex-direction:column;
    gap:10px;
    align-items: center;
  }
`;

const FileDetails = ({ fileId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [usedInCourses, setUsedInCourses] = useState(null);

  const { deleteFile } = useMediaActions();
  const { fileCourseInfo } = useFileCourseInfo(fileId);

  const file = fileCourseInfo?.file || null;

  useEffect(() => {
    if (fileCourseInfo) {
      setUsedInCourses(fileCourseInfo.courses || []);
    }
  }, [fileCourseInfo]);

  const closeModal = useCallback(() => {
    navigate(-1)
  }, [navigate]);

  const handleDeleteDialog = useCallback(() => {
    setDialogOpen(!dialogOpen)
  }, [dialogOpen]);

  const onDeleteFile = useCallback(async () => {
    if (file) {
      await deleteFile({ id: file.id });
      closeModal();
    }
    setDialogOpen(!dialogOpen)
  }, [deleteFile, file, setDialogOpen, dialogOpen, closeModal]);

  return (
    <Modal
      open={!!fileId}
      width='1100px'
      maxWidth='lg'
      onClose={closeModal}
      dialogActionsChildren={<>
        <FileContent>
          <Flex direction={'column'} align={'flex-start'}>
            <Typo.Text><strong>{t('globals.name')}:</strong> {file?.name || 'N/A'}</Typo.Text>
            <Typo.Text><strong>{t('globals.created')}:</strong> {file?.created || 'N/A'}</Typo.Text>
            <Typo.Text><strong>{t('globals.size')}:</strong> {file?.size ? (file.size / 1000000).toFixed(4) + 'MB' : 'N/A'}</Typo.Text>
            {/*<Typo.Text><strong>In courses:</strong> {usedInCourses?.length ? usedInCourses?.toString() : 'none'}</Typo.Text>*/}
          </Flex>
          <Flex gap='20'>
            <DownloadableFile.AsButton label={t('globals.download')} file={file}/>
            <Button icon='Delete' onClick={handleDeleteDialog}>{t('globals.delete')}</Button>
          </Flex>
        </FileContent>

        <ConfirmDelete
          open={dialogOpen}
          title={t('media.deleteConfirmationTitle')}
          description={t('media.deleteConfirmationDescription')}
          onClose={handleDeleteDialog}
          onAcceptDelete={onDeleteFile}
        /></>}
    >
      {file?.type === 'video' && <MediaPlayer previewContent={file?.url} />}
      {file?.type === 'audio' && <MediaPlayer width='100%' height='100px' previewContent={file?.url} isAudio={true} />}
      {file?.type === 'image' && <Image> <img alt={'file'} id={file?.id} src={file?.url} loading='lazy' /> </Image>}
      {file?.type === 'other' &&
        <MyDocViewer
          documents={[{ uri: file.url, fileType: file.mime }]}
          prefetchMethod={'GET'}
          config={{
            header: {
              disableHeader: true,
              disableFileName: true,
              retainURLParams: false
            }
          }}
          pluginRenderers={DocViewerRenderers}
        />
      }
    </Modal >
  )
}

export default FileDetails;
